RivalIQ.module('state', function (state, App, Backbone, Marionette, $, _) {

    'use strict';

    const user = state.user = new RivalIQ.models.User({
        url: '/api/users'
    });

    state.utils.overrideStateGet(user, {
        pendingDownloads: _.identity
    });

    state.utils.overrideStateSet(user, {
        pendingDownloads: _.identity
    });

    user.addPendingDownload = function (model) {
        const me = this;
        me.get('pendingDownloads').add(model);
    };

    // reorganize some enums for use later
    let conditionalEmailTypes = null;

    function getConditionalEmailTypes() {
        conditionalEmailTypes = conditionalEmailTypes || _(RivalIQ.enums.email.details)
            .filter('conditionalDefault')
            .map('dashboardEmailTypeId')
            .value();

        return conditionalEmailTypes;
    }

    let emailTypesBySection = null;

    function getEmailTypesBySection() {
        emailTypesBySection = emailTypesBySection || _.reduce(RivalIQ.enums.email.section, function (memo, section) {
            const sectionEmailTypes = _.map(_.filter(_.omit(RivalIQ.enums.email.details, getConditionalEmailTypes()), function (emailDetails) {
                return (emailDetails.section === section);
            }), 'dashboardEmailTypeId');
            memo[section] = sectionEmailTypes;
            return memo;
        }, {});

        return emailTypesBySection;
    }

    function recalculateDisabledSections(emailSettings) {
        return _.reduce(getEmailTypesBySection(), function (memo, emailTypes, section) {
            memo[section] = _.every(emailTypes, function (type) {
                if (_.isBoolean(emailSettings[type])) {
                    return !emailSettings[type];
                } else {
                    return !RivalIQ.enums.email.defaultSettings[type];
                }
            });
            return memo;
        }, {});
    }

    function getLandscapeEmailPersistence(properties) {
        const landscapeId = RivalIQ.state.view.get('landscapeId');

        // make sure landscapes is an object! If it's not, lodash will create an array of length (landscapeId+1)!
        if (!RivalIQ.settings.userSettings.landscapes) {
            RivalIQ.settings.userSettings.landscapes = {};
        }

        _.set(RivalIQ.settings.userSettings, ['landscapes', landscapeId, 'email', 'enabled'], properties.emailSettings);
        _.set(RivalIQ.settings.userSettings, ['landscapes', landscapeId, 'email', 'disabledSections'], recalculateDisabledSections(properties.emailSettings));

        return RivalIQ.settings.userSettings.landscapes;
    }

    user.setEmailSetting = function (type, value) {
        const me = this;
        const emailSettings = me.get('emailSettings');
        emailSettings[type] = Boolean(value);

        me.set('emailSettings', emailSettings);
    };

    function getEmailDefaults(emailEnabledDefault, disabledEmailSections) {
        const defaultEmailSettings = {};

        // if we don't have a user level override, use the system defaults
        // otherwise set them all one way or the other (typically all off)
        if (!_.isBoolean(emailEnabledDefault)) {
            _.extend(defaultEmailSettings, RivalIQ.enums.email.defaultSettings);
        } else {
            _.each(RivalIQ.enums.email.type, function (typeId) {
                defaultEmailSettings[typeId] = emailEnabledDefault;
            });
        }

        // these email types should default to disabled only if the section they belong to is disabled
        if (_.isEmpty(disabledEmailSections)) {
            disabledEmailSections = recalculateDisabledSections(defaultEmailSettings);
        }
        _.each(getConditionalEmailTypes(), function (emailType) {
            const emailSection = RivalIQ.enums.email.details[emailType].section;
            defaultEmailSettings[emailType] = !disabledEmailSections[emailSection];
        });

        return defaultEmailSettings;
    }


    user.getEmailDefaults = function () {
        const me = this;

        const emailEnabledDefault = me.get('email_enabled_default');
        const disabledEmailSections = me.get('disabledEmailSections');
        return getEmailDefaults(emailEnabledDefault, disabledEmailSections);
    };

    user.hasValidCredentials = function (source) {
        const me = this;
        source = (_.isString(source) ? RivalIQ.data.CompareData.getSource(source) : source);
        const details = RivalIQ.data.CompareData.sourceDetails[source];

        return _.some(me.get(`${details.channel}TokenSummary`), function (token) {
            const channelToken = (token.expiresAt ? new Date(token.expiresAt) : null);
            return (channelToken !== null && channelToken > new Date());
        });
    };

    /**
     * Returns a boolean indicating if the tour should be shown for this user.
     * @param tour The tour to test to be shown.
     */
    user.shouldShowTour = function (tour) {

        const me = this;
        const completed = _.get(me.get('tours'), tour, false);
        const isImpersonating = me.get('isImpersonating');
        return (!completed && !App.isHeadless && !isImpersonating);
    };

    user.completeTour = function (tour) {
        const me = this;

        const tours = me.get('tours');
        tours[tour] = true;
        me.set('tours', tours);
    };

    user.updateClientTimeZone = function () {
        const me = this;

        const current = me.get('clientTimeZone');

        // e.g., Mon Mar 21 2016 14:26:00 GMT-0700 (PDT)
        const timeZone = RivalIQ.utils.timezone.getClientTimezoneString();

        if (timeZone && current !== timeZone) {
            me.set('clientTimeZone', timeZone);
        }
    };

    user.updateActivity = function () {
        const me = this;
        return $.post(`/api/users/${me.get('id')}/activity`);
    };

    /**
     * A property map transforming settings into view state, and view state
     * into persistence.
     * @type {*[]}
     */
    const statePropertyMap = [
        {
            settingProperty: 'id',
            stateProperty: 'id',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'account_id',
            stateProperty: 'account_id',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'email',
            stateProperty: 'email',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'first_name',
            stateProperty: 'first_name',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'last_name',
            stateProperty: 'last_name',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'terms_and_conditions',
            stateProperty: 'terms_and_conditions',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'data_version',
            stateProperty: 'data_version',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'metadata_version',
            stateProperty: 'metadata_version',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'landscapes',
            stateProperty: 'landscapes',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'api_key',
            stateProperty: 'api_key',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'phone_number',
            stateProperty: 'phone_number',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'role_id',
            stateProperty: 'role_id',
            path: 'user',
            immutable: true
        },
        {
            settingProperty: 'pendingDownloads',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'linked_in_token_summary',
            path: null,
            stateProperty: 'linkedinTokenSummary',
            immutable: true
        },
        {
            settingProperty: 'facebook_token_summary',
            path: null,
            stateProperty: 'facebookTokenSummary',
            immutable: true
        },
        {
            settingProperty: 'tiktok_token_summary',
            path: null,
            stateProperty: 'tiktokTokenSummary',
            immutable: true
        },
        {
            settingProperty: 'twitter_token_summary',
            path: null,
            stateProperty: 'twitterTokenSummary',
            immutable: true
        },
        {
            settingProperty: 'has_set_password',
            path: null,
            stateProperty: 'hasSetPassword',
            immutable: true
        },
        {
            settingProperty: 'impersonatorId',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'email_enabled_default',
            path: 'user',
            stateProperty: 'email_enabled_default',
            persistenceProperty: 'email_enabled_default'
        },
        {
            settingProperty: 'emailSettings',
            path: null,
            persistenceProperty: 'landscapeSettings',
            persistenceSerializer: getLandscapeEmailPersistence
        },
        {
            settingProperty: 'disabledEmailSections',
            path: null,
            persistenceProperty: 'landscapeSettings',
            persistenceSerializer: getLandscapeEmailPersistence
        },
        {
            settingProperty: 'userStatus',
            stateProperty: 'account_user_status_id',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'tours',
            default: {}
        },
        {
            settingProperty: 'showOnboardingDialog',
            default: false
        },
        {
            settingProperty: 'estimatedImpressionsUpsellDismissed',
            default: false
        },
        {
            settingProperty: 'widgets',
            default: {}
        },
        {
            settingProperty: 'notifications',
            default: {}
        },
        {
            settingProperty: 'clientTimeZone',
            default: null
        },
        {
            settingProperty: 'lastSelectedAdAccounts',
            default: {}
        }, // maps landscape id -> last selected ad account.
        {
            settingProperty: 'lastSelectedFacebookCompanies',
            default: {}
        }, // maps landscape id -> last selected Facebook presence.
        {
            settingProperty: 'lastSelectedInstagramCompanies',
            default: {}
        }, // maps landscape id -> last selected instagram presence.
        {
            settingProperty: 'lastSelectedTwitterCompanies',
            default: {}
        }, // maps landscape id -> last selected Twitter presence.
        {
            settingProperty: 'lastSelectedLinkedinCompanies',
            default: {}
        }, // maps landscape id -> last selected linkedin presence.
        {
            settingProperty: 'lastSelectedSocialPostsLayout',
            default: {}
        },
        {
            settingProperty: 'sessions',
            path: 'user',
            stateProperty: 'sessions',
            immutable: true
        },
        {
            settingProperty: 'timeOfLastIntercomMessage',
            default: null
        }
    ];

    /**
     * Initializes user state from properties.
     * @param user
     * @param properties
     */
    function initializeUserFromProperties(user, properties) {
        const Exports = RivalIQ.export.collections.Exports;

        // Set landscapes default if undefined
        _.defaults(properties.userSettings, {
            landscapes: {}
        });

        properties = _.extend({}, properties, {
            pendingDownloads: new Exports(properties.pendingDownloads || [], {
                pollStatus: true,
                downloadIfDone: true,
                removeOnComplete: true
            })
        });

        properties.disabledEmailSections = _.get(properties.userSettings, ['landscapes', properties.activeLandscapeId, 'email', 'disabledSections'], {});
        const defaultEmailSettings = getEmailDefaults(_.get(properties, ['user', 'email_enabled_default']), properties.disabledEmailSections);
        properties.emailSettings = _.get(properties.userSettings, ['landscapes', properties.activeLandscapeId, 'email', 'enabled'], {});
        const originalEmailSettings = _.clone(properties.emailSettings);
        _.defaults(properties.emailSettings, defaultEmailSettings);

        state.utils.initializeState('user', 'userSettings', statePropertyMap, user, properties);
        if (!_.isEqual(originalEmailSettings, properties.emailSettings)) {
            // ensure that the db user state exactly matches initialized state
            state.utils.persistState('user', statePropertyMap, user);
        }
    }

    (function () {

        let hasInitialized = false;

        user.initialize = function (properties) {

            if (hasInitialized) {

                throw 'not supported: user cannot be initialized more than once.';
            }

            initializeUserFromProperties(this, properties);

            hasInitialized = true;

            user.updateClientTimeZone();
        };

    }());

});
